import classnames from 'classnames/bind';
import {
  Chart as ChartJS,
  LinearScale,
  LineElement,
  Tooltip,
  TimeSeriesScale,
  BarElement,
  PointElement,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import 'chartjs-adapter-luxon';

import { DataWidget } from 'types/dashboard_widget';

import { TIME_UNIT_MAP } from 'dashboards/types/time_unit';
import { resolveGranularity } from 'dashboards/services/granularity';
import { createCsvFile } from 'services/csv';
import { downloadFile } from 'services/download_file';
import { getDimensionsFromMetricsResponse } from 'dashboards/services/metric';
import { hslaToString, MAIN_CHART_COLOR } from 'dashboards/services/color';
import { rowsToTimezone } from 'dashboards/services/table';
import { sanitizeDataWidgetFilters } from 'dashboards/components/DataWidgetFiltersInput/sanitize';
import { useTimeRangeParams } from 'dashboards/hooks/useTimeRangeParams';
import { useTimeSeries } from 'dashboards/hooks/useTimeSeries';
import { useTopLevelDataWidgetFilters } from 'dashboards/hooks/useTopLevelDataWidgetFilters';
import { useUserMetrics } from 'dashboards/hooks/useUserMetrics';

import { ChartError } from 'dashboards/components/ChartError/ChartError';
import { ChartLoader } from 'dashboards/components/ChartLoader/ChartLoader';
import { Icon } from 'components/Icon/IconDeprecated';
import { Widget } from 'dashboards/widgets/Widget/Widget';

import styles from './Chart.module.scss';

const c = classnames.bind(styles);

ChartJS.register(
  BarElement,
  LineElement,
  PointElement,
  LinearScale,
  Tooltip,
  TimeSeriesScale
);

type TimeSeriesProps = {
  widget: DataWidget<'timeseries-bar'> | DataWidget<'timeseries-line'>;
  variant: 'bar' | 'line';
};

export function TimeSeries({ widget, variant }: TimeSeriesProps) {
  const { metric, granularity: rawGranularity, ...query } = widget.query;

  const { start, stop, timezone } = useTimeRangeParams();

  const granularity = resolveGranularity(rawGranularity, { start, stop });

  const { data: userMetrics } = useUserMetrics();

  const columns = getDimensionsFromMetricsResponse(metric, userMetrics);

  const filters = useTopLevelDataWidgetFilters({
    filters: sanitizeDataWidgetFilters(widget.query.filters),
    columns,
  });

  const { data, isLoading, error } = useTimeSeries({
    params: {
      metricName: metric,
      timeRange: { start, stop },
      timeZone: timezone,
      granularity,
      ...query,
      filters,
    },
  });

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: 'timeseries' as const,
        time: {
          unit: TIME_UNIT_MAP[granularity],
        },
        adapters: {
          date: {
            zone: timezone,
          },
        },
      },
      y: { beginAtZero: true },
    },
    plugins: {
      legend: {
        display: false,
      },
    },
  };

  if (error) {
    return (
      <Widget widget={widget}>
        <ChartError />
      </Widget>
    );
  }

  if (!data && isLoading) {
    return (
      <Widget widget={widget}>
        <ChartLoader />
      </Widget>
    );
  }

  if (!data) {
    return null;
  }

  // FIXME Remove this once fixed in api-server
  if (data.length === 0) {
    return (
      <Widget widget={widget}>
        <ChartError error="No data available yet." />
      </Widget>
    );
  }

  const [{ labels, values }] = data;
  const datasets = data.map(({ values }) => ({
    borderRadius: 2,
    backgroundColor: hslaToString(MAIN_CHART_COLOR),
    borderColor: hslaToString(MAIN_CHART_COLOR),
    data: values,
  }));

  const csv = {
    headers: ['timestamp', 'value'],
    rows: labels.map((timestamp, i) => [timestamp, String(values[i])]),
  };

  const menuItems = {
    csv: {
      children: 'Export as CSV',
      leftSection: <Icon name="export" size="small" />,
      onClick: () =>
        downloadFile(
          widget.name,
          createCsvFile(rowsToTimezone({ ...csv, timezone }))
        ),
      disabled: csv.rows.length === 0,
    },
  };

  if (variant === 'bar') {
    return (
      <Widget widget={widget} menuItems={menuItems}>
        <div className={c('chart')}>
          <Bar options={options} data={{ labels, datasets }} />
        </div>
      </Widget>
    );
  }

  return (
    <Widget widget={widget} menuItems={menuItems}>
      <div className={c('chart')}>
        <Line options={options} data={{ labels, datasets }} />
      </div>
    </Widget>
  );
}
